// DTOs del flujo de validación de paros por mantenimiento.
//
// Modelo (docs/analisis/paros-mantenimiento.md §7):
//   - El estado del paro frente a mantenimiento es DERIVADO, nunca persistido:
//     hora_fin + validado_mtto_at + existencia de corrección viva.
//   - `validaciones_paro` guarda el sello del técnico (tipo='validacion') y las
//     propuestas de corrección (tipo='correccion') con su ciclo
//     propuesta → aprobada/rechazada. Una sola corrección viva por paro
//     (UNIQUE parcial en BD).
//   - La corrección aprobada se aplica como UPDATE al paro en la transacción
//     de aprobación del supervisor — el técnico jamás escribe registros_paro
//     por esta vía. El único write directo del técnico es el CIERRE de un paro
//     abierto (hora_fin backdateable, nunca futura en el marco fake-UTC del
//     tenant).
import { z } from 'zod';
const boolQueryParam = (def) => z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .default(def)
    .transform((v) => v === true || v === 'true');
// =============================================================================
// Fila de validaciones_paro
// =============================================================================
// 'confirmacion' = sello del SUPERVISOR sobre el paro (mig 079). Vive en la
// misma tabla que el sello del técnico para que el ciclo tenga UNA sola línea
// de tiempo por paro.
export const tipoValidacionParoSchema = z.enum(['validacion', 'correccion', 'confirmacion']);
export const estadoValidacionParoSchema = z.enum(['propuesta', 'aprobada', 'rechazada']);
export const validacionParoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    registro_paro_id: z.string().uuid(),
    tipo: tipoValidacionParoSchema,
    estado: estadoValidacionParoSchema,
    causal_madre_id_prop: z.string().uuid().nullable(),
    causal_id_prop: z.string().uuid().nullable(),
    hora_inicio_prop: z.string().datetime({ offset: true }).nullable(),
    hora_fin_prop: z.string().datetime({ offset: true }).nullable(),
    razon: z.string(),
    creado_por_user_id: z.string().uuid(),
    resuelto_por_user_id: z.string().uuid().nullable(),
    resuelto_at: z.string().datetime({ offset: true }).nullable(),
    razon_resolucion: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
});
// =============================================================================
// Estado derivado + item de la cola/drill del técnico
// =============================================================================
export const estadoParoMttoSchema = z.enum([
    'abierto',
    'pendiente',
    'correccion_propuesta',
    'validado',
    'validado_corregido',
]);
/** Resumen de la corrección viva (tipo='correccion', estado='propuesta'). */
export const correccionVivaResumenSchema = z.object({
    id: z.string().uuid(),
    razon: z.string(),
    created_at: z.string().datetime({ offset: true }),
});
/**
 * Un paro visto desde mantenimiento: contexto completo del registro padre
 * (línea, planta, fecha, turno, líder) + causal jerárquica + estado derivado.
 */
export const paroMttoItemSchema = z.object({
    id: z.string().uuid(),
    registro_produccion_hora_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    linea_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    fecha: z.string().date(),
    turno_numero: z.number().int().nullable(),
    turno_nombre: z.string().nullable(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }).nullable(),
    minutos_perdidos: z.number().nullable(),
    causal_madre: z.object({ id: z.string().uuid(), nombre: z.string() }),
    causal: z.object({ id: z.string().uuid(), nombre: z.string() }),
    categoria: z.object({
        id: z.string().uuid(),
        nombre: z.string(),
        es_mantenimiento: z.boolean(),
        afecta_disponibilidad: z.boolean(),
    }),
    observaciones: z.string().nullable(),
    lider: z.object({ id: z.string().uuid(), nombre: z.string(), apellido: z.string() }).nullable(),
    estado_mtto: estadoParoMttoSchema,
    validado_mtto_at: z.string().datetime({ offset: true }).nullable(),
    cerrado_por_mtto: z.boolean(),
    orden_trabajo_id: z.string().uuid().nullable(),
    correccion_viva: correccionVivaResumenSchema.nullable(),
});
// =============================================================================
// GET /api/mantenimiento/paros/pendientes
// =============================================================================
export const parosPendientesQuerySchema = z.object({
    planta_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
    /** true (default) = solo categorías con es_mantenimiento (D1, con toggle). */
    solo_mantenimiento: boolQueryParam(true),
    /** true = incluye también los ya validados (para revisión histórica). */
    incluir_validados: boolQueryParam(false),
    /** Default del rango: día operativo actual hacia atrás 7 días. */
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
});
export const parosPendientesResponseSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    /** True si el filtro por categorías de mantenimiento pudo aplicarse. */
    filtro_mantenimiento_aplicado: z.boolean(),
    items: paroMttoItemSchema.array(),
    conteos: z.object({
        pendientes: z.number().int().min(0),
        con_correccion_propuesta: z.number().int().min(0),
    }),
});
// =============================================================================
// PATCH /api/mantenimiento/paros/:id/cerrar — cierre directo por el técnico
// =============================================================================
export const paroCerrarTecnicoSchema = z
    .object({
    /**
     * Hora de cierre en el marco fake-UTC del tenant (pared local etiquetada
     * Z, misma convención de la captura). Backdateable; el server valida
     * hora_fin > hora_inicio y hora_fin <= ahora del tenant.
     */
    hora_fin: z.string().datetime({ offset: true }),
    observaciones: z.string().trim().max(500).nullable().default(null),
})
    .refine((p) => p.observaciones === null || p.observaciones.length >= 5, {
    message: 'La observación debe tener mínimo 5 caracteres',
    path: ['observaciones'],
});
export const paroCerradoMttoResponseSchema = z.object({
    id: z.string().uuid(),
    hora_fin: z.string().datetime({ offset: true }),
    minutos_perdidos: z.number(),
    validado_mtto_at: z.string().datetime({ offset: true }),
    cerrado_por_user_id: z.string().uuid(),
});
// =============================================================================
// POST /api/mantenimiento/paros/:id/validar — sello directo (sin cambios)
// =============================================================================
export const paroValidarResponseSchema = z.object({
    registro_paro_id: z.string().uuid(),
    validado_mtto_at: z.string().datetime({ offset: true }),
    validacion_id: z.string().uuid(),
});
// =============================================================================
// POST /api/mantenimiento/paros/:id/correcciones — propuesta del técnico
// =============================================================================
export const correccionParoProponerSchema = z
    .object({
    /** Causal propuesta: madre y específica viajan JUNTAS (jerarquía). */
    causal_madre_id: z.string().uuid().optional(),
    causal_id: z.string().uuid().optional(),
    hora_inicio: z.string().datetime({ offset: true }).optional(),
    hora_fin: z.string().datetime({ offset: true }).optional(),
    razon: z
        .string()
        .trim()
        .min(10, 'Describa la razón de la corrección (mínimo 10 caracteres)')
        .max(500, 'Máximo 500 caracteres'),
})
    .refine((p) => p.causal_madre_id !== undefined ||
    p.causal_id !== undefined ||
    p.hora_inicio !== undefined ||
    p.hora_fin !== undefined, { message: 'Debe proponer al menos un cambio (causal u horas)' })
    .refine((p) => (p.causal_madre_id === undefined) === (p.causal_id === undefined), {
    message: 'La causal madre y la específica deben proponerse juntas',
    path: ['causal_id'],
});
// =============================================================================
// POST /api/mantenimiento/paros/:id/orden-trabajo — ligar o crear OT
// =============================================================================
export const paroOrdenTrabajoInputSchema = z
    .object({
    /** Ligar a una OT existente (XOR con crear una nueva). */
    orden_trabajo_id: z.string().uuid().optional(),
    /** Crear OT nueva: activo explícito (si la línea tiene varios/ninguno). */
    activo_id: z.string().uuid().optional(),
    descripcion: z.string().trim().min(5).max(500).optional(),
})
    .refine((p) => !(p.orden_trabajo_id && (p.activo_id || p.descripcion)), {
    message: 'Ligue una OT existente O cree una nueva, no ambas',
    path: ['orden_trabajo_id'],
});
export const paroOrdenTrabajoResponseSchema = z.object({
    orden_trabajo_id: z.string().uuid(),
    codigo: z.string(),
    /** True si la OT se creó en esta llamada (false = se ligó una existente). */
    creada: z.boolean(),
});
//# sourceMappingURL=mantenimiento_paros_validacion.js.map